import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  InventoryTagEvent,
  InventoryTagEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/inventory/v1/inventory_tag_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { InventoryTagCreatedDetail } from './InventoryTagCreatedDetail';
import { InventoryTagDeletedDetail } from './InventoryTagDeletedDetail';
import { InventoryTagUpdatedDetail } from './InventoryTagUpdatedDetail';

import { useTranslations } from '@/inventory/i18n/useTranslations';

type InventoryTagEventDetailProps = {
  payload: string;
};

export const InventoryTagEventDetail = ({
  payload,
}: InventoryTagEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): InventoryTagEvent | null => {
    try {
      return fromJson(InventoryTagEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <InventoryTagCreatedDetail event={inner.value} />;
      case 'updated':
        return <InventoryTagUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <InventoryTagDeletedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.inventoryTagCreated;
      case 'updated':
        return t.logs.events.inventoryTagUpdated;
      case 'deleted':
        return t.logs.events.inventoryTagDeleted;
      case undefined:
      default:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@InventoryTagEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
